import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import projectService from '../api/projectService';
import equipmentService from '../api/equipmentService';
import Modal from '../components/Modal';
import ProjectForm from '../components/ProjectForm';
import Button from '../components/Button';
import RowActions from '../components/RowActions';
// Escala semántica de estado — fuente única de verdad (ver src/theme/status.js)
import { PROJECT_STATUS_TONE, getStatusBadgeStyle as getBadgeStyleForTone } from '../theme/status';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';

// Etiquetas legibles para cada estado de proyecto
const STATUS_LABELS = {
  active: 'Activo',
  inactive: 'Inactivo',
  completed: 'Completado',
};

/**
 * @description Página para la gestión de Proyectos.
 * Permite ver, crear, editar y eliminar proyectos.
 * Incluye búsqueda por texto y filtro por estado en cliente.
 * @returns {JSX.Element}
 */
const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga inicial — bloquea el render de la tabla
  const [loadError, setLoadError] = useState('');
  // Error de operación (guardar/eliminar) — se muestra encima de la tabla sin bloquear
  const [actionError, setActionError] = useState('');
  // Mapa { project_id → cantidad de equipos }; null mientras carga, número cuando resuelve
  const [equipmentCounts, setEquipmentCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Paginación
  const LIMIT = 20;
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  // Estado de búsqueda y filtro
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  /**
   * @description Carga el conteo de equipos de cada proyecto en paralelo.
   * Usa allSettled para que un fallo individual no bloquee los demás resultados.
   * @param {Array} projectList Lista de proyectos ya cargados.
   */
  const fetchEquipmentCounts = async (projectList) => {
    if (!projectList || projectList.length === 0) return;
    setCountsLoading(true);
    const requests = projectList.map((p) =>
      equipmentService.getEquipmentCountByProject(p.project_id)
        .then((count) => ({ project_id: p.project_id, count }))
        .catch(() => ({ project_id: p.project_id, count: null }))
    );
    const results = await Promise.all(requests);
    const countsMap = {};
    results.forEach(({ project_id, count }) => {
      countsMap[project_id] = count;
    });
    setEquipmentCounts(countsMap);
    setCountsLoading(false);
  };

  /**
   * @description Carga la lista de proyectos desde la API y actualiza el estado.
   * Luego dispara las peticiones de conteo de equipos en paralelo.
   */
  const fetchProjects = useCallback(async (targetPage = 1) => {
    setLoadError('');
    try {
      const response = await projectService.getProjects({ page: targetPage, limit: LIMIT });
      const projectList = response.data.data;
      setProjects(projectList);
      setMeta(response.data.meta);
      fetchEquipmentCounts(projectList);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      setLoadError('No se pudo cargar la lista de proyectos. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  /**
   * @description Muestra un error de operación y lo limpia automáticamente tras 6 segundos.
   * @param {string} message - Mensaje de error a mostrar.
   */
  const showActionError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(''), 6000);
  };

  /**
   * @description Maneja el guardado (creación o actualización) de un proyecto.
   * @param {object} projectData Los datos del proyecto provenientes del formulario.
   */
  const handleSave = async (projectData) => {
    try {
      if (currentProject) {
        await projectService.updateProject(currentProject.project_id, projectData);
      } else {
        await projectService.createProject(projectData);
      }
      fetchProjects();
      closeModal();
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
      showActionError(error.response?.data?.message || 'No se pudo guardar el proyecto.');
    }
  };

  /**
   * @description Maneja la eliminación de un proyecto tras confirmación.
   * @param {number | string} id El ID del proyecto a eliminar.
   */
  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const executeDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await projectService.deleteProject(id);
      fetchProjects();
    } catch (error) {
      console.error('Error al eliminar proyecto:', error);
      showActionError(error.response?.data?.message || 'No se pudo eliminar el proyecto.');
    }
  };

  /**
   * @description Abre el modal para crear o editar un proyecto.
   * @param {object | null} project El proyecto a editar, o `null` para crear uno nuevo.
   */
  const openModal = (project = null) => {
    setCurrentProject(project);
    setShowModal(true);
  };

  /**
   * @description Cierra el modal y resetea el proyecto actual.
   */
  const closeModal = () => {
    setCurrentProject(null);
    setShowModal(false);
  };

  // Filtrado en memoria: combina búsqueda por texto y filtro por estado
  const filteredProjects = projects.filter((p) => {
    const matchesStatus = statusFilter ? p.status === statusFilter : true;
    const query = searchText.toLowerCase();
    const matchesSearch =
      query === '' ||
      p.name?.toLowerCase().includes(query) ||
      p.client_name?.toLowerCase().includes(query) ||
      p.address?.toLowerCase().includes(query) ||
      p.client_email?.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  if (loading) return <LoadingState message="Cargando proyectos..." />;

  // Error de carga inicial: no tiene sentido mostrar la tabla sin datos
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Proyectos</h2>

      {/* Alerta de error de operación: visible sobre la tabla, se cierra manualmente o tras 6s */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            style={styles.actionErrorClose}
            aria-label="Cerrar mensaje de error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Barra de acciones: búsqueda + filtro por estado + botón crear */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por nombre, cliente o dirección..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
          <option value="completed">Completado</option>
        </select>
        {/* Acción primaria de la página: relleno sólido en teal de marca */}
        <Button variant="primary" onClick={() => openModal()} style={{ marginLeft: 'auto' }}>
          Crear Nuevo Proyecto
        </Button>
      </div>

      {/* Contador de resultados visibles */}
      <p style={styles.resultCount}>
        {filteredProjects.length} {filteredProjects.length === 1 ? 'proyecto encontrado' : 'proyectos encontrados'}
      </p>

      {/* Estado de lista vacía */}
      {filteredProjects.length === 0 ? (
        <p style={styles.emptyMessage}>
          {searchText || statusFilter
            ? 'No se encontraron proyectos con los filtros aplicados.'
            : 'No hay proyectos registrados.'}
        </p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Nombre</th>
              <th style={styles.th} className="col-hide-mobile">Dirección</th>
              <th style={styles.th}>Cliente</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th} className="col-hide-mobile">Teléfono</th>
              <th style={styles.th} className="col-hide-mobile">Email</th>
              <th style={styles.th} className="col-hide-mobile col-numeric">Cant. equipos</th>
              <th style={styles.th} className="col-actions-sticky">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map((p) => (
              <tr key={p.project_id} style={styles.row}>
                <td style={styles.td}>{p.name}</td>
                <td style={styles.td} className="col-hide-mobile">{p.address || '—'}</td>
                <td style={styles.td}>{p.client_name}</td>
                <td style={styles.td}>
                  <span style={getStatusBadgeStyle(p.status)}>
                    {STATUS_LABELS[p.status] || p.status}
                  </span>
                </td>
                <td style={styles.td} className="col-hide-mobile">{p.client_phone || '—'}</td>
                <td style={styles.td} className="col-hide-mobile">{p.client_email || '—'}</td>
                <td style={styles.td} className="col-hide-mobile col-numeric">
                  {countsLoading && equipmentCounts[p.project_id] === undefined
                    ? <span style={styles.countLoading}>...</span>
                    : equipmentCounts[p.project_id] === null
                    ? <span style={styles.countError} title="No se pudo obtener el conteo">—</span>
                    : (
                      /* Enlace a /equipment filtrado por este proyecto */
                      <Link
                        to={`/equipment?projectId=${p.project_id}`}
                        style={styles.countBadgeLink}
                      >
                        {equipmentCounts[p.project_id] ?? 0}
                      </Link>
                    )
                  }
                </td>
                <td style={styles.td} className="col-actions-sticky">
                  {/* Editar: tinte teal, acción esperada. Eliminar: solo-ícono,
                      neutro en reposo y rojo recién al hover — ver RowActions.js */}
                  <RowActions
                    onEdit={() => openModal(p)}
                    onDelete={() => handleDelete(p.project_id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Controles de paginación */}
      {meta.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => fetchProjects(meta.page - 1)}
            disabled={meta.page <= 1}
            style={{ ...styles.pageButton, opacity: meta.page <= 1 ? 0.4 : 1 }}
          >
            ← Anterior
          </button>
          <span style={styles.pageInfo}>
            Página {meta.page} de {meta.totalPages} &middot; {meta.total} resultado{meta.total !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => fetchProjects(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            style={{ ...styles.pageButton, opacity: meta.page >= meta.totalPages ? 0.4 : 1 }}
          >
            Siguiente →
          </button>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={closeModal}
        title={currentProject ? 'Editar Proyecto' : 'Crear Proyecto'}
      >
        <ProjectForm project={currentProject} onSave={handleSave} onCancel={closeModal} />
      </Modal>

      <Modal
        show={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Eliminar proyecto"
      >
        <p style={{ margin: 0, color: '#444' }}>
          ¿Está seguro de que desea eliminar este proyecto? Dejará de aparecer en el sistema, pero su historial (órdenes y reportes cerrados) se conserva.
        </p>
        <div className="modal-footer">
          <button onClick={() => setConfirmDeleteId(null)} className="modal-btn-secondary">
            Cancelar
          </button>
          <button onClick={executeDelete} className="modal-btn-danger">
            Eliminar
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * @description Genera el estilo del badge de estado del proyecto.
 * Dominio distinto al de las OTs (active/inactive/completed vs. el enum de OrderStatus),
 * pero delega en la MISMA escala de tonos compartida (theme/status.js): un verde
 * siempre es el mismo verde, un gris siempre es el mismo gris, en toda la app.
 * @param {string} status - Valor del estado (active, inactive, completed).
 * @returns {object} Objeto de estilos inline.
 */
const getStatusBadgeStyle = (status) => getBadgeStyleForTone(PROJECT_STATUS_TONE[status]);

// Estilos de la página, consistentes con Users.js
const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '8px',
    minWidth: '260px',
    flex: '1',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  filterSelect: {
    padding: '8px',
    minWidth: '180px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
  // Badge-enlace numérico para la cantidad de equipos — navega a /equipment?projectId=X
  countBadgeLink: {
    display: 'inline-block',
    minWidth: '24px',
    padding: '2px 8px',
    borderRadius: '12px',
    backgroundColor: '#e8f4fd',
    color: '#1a6496',
    border: '1px solid #bce0fd',
    fontSize: '0.85rem',
    fontWeight: 'bold',
    textAlign: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  // Indicador mientras se carga el conteo
  countLoading: {
    color: '#aaa',
    fontSize: '0.85rem',
  },
  // Indicador de error al obtener el conteo individual
  countError: {
    color: '#bbb',
    fontSize: '0.85rem',
    cursor: 'default',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 0',
    marginTop: '8px',
  },
  pageButton: {
    padding: '6px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#374151',
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  // Banner de error para operaciones fallidas (guardar, eliminar)
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fdecea',
    color: '#c0392b',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  actionErrorClose: {
    background: 'none',
    border: 'none',
    color: '#c0392b',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: '1',
    padding: '0 0 0 12px',
    fontWeight: 'bold',
  },
};

export default Projects;
